import { useState, useCallback, useEffect } from 'react';
import { apiFetch, track } from '../dppApi';

// Manage Catalogue: add products to the brand's catalogue three ways —
//   • Upload CSV   (name, image_url, certificate_urls[';'-separated], price, currency)
//   • Paste JSON   (the same shape a programmatic API caller would POST)
//   • Manual       (single product; image + certificates uploaded as files)
// CSV/JSON go to POST /api/products/import (links fetched server-side). Manual
// creates the product on submit (QR minted then — not before) and uploads the
// image + certs as files. Descriptions are left for Bloom Assist to fill.
// Section 1: create new products (+ passports). Section 2: enrich existing ones.
const CREATE_TABS = [
    { key: 'csv', label: 'Ingest CSV' },
    { key: 'json', label: 'Paste JSON' },
    { key: 'manual', label: 'Manual Upload' },
];

const ENRICH_TABS = [
    { key: 'enrich', label: 'Fill in missing details' },
    { key: 'enhance', label: 'Enhance existing descriptions' },
    { key: 'images', label: 'Edit images' },
];

// The two image-enhancement presets offered under "Enhance product images".
const IMAGE_MODES = [
    { key: 'match_catalogue', label: 'Match existing catalogue', hint: "Restyle the background to match your other pieces." },
    { key: 'elevated_white', label: 'Elevated white', hint: 'Clean, pure-white catalogue background.' },
];

const SECTION_TABS = { create: CREATE_TABS, enrich: ENRICH_TABS };

// A section always opens on its FIRST pill — there is no "nothing selected"
// state. Derived from the arrays above, so reordering the pills moves the
// default with them rather than leaving a stale hard-coded key behind.
const firstTabOf = (s) => SECTION_TABS[s][0].key;

// Heading shown atop each option's detail panel (keeps every panel consistent).
const DETAIL_TITLES = {
    csv: 'Ingest CSV',
    json: 'Paste JSON',
    manual: 'Manual Upload',
    enrich: 'Fill in missing details',
    enhance: 'Enhance existing descriptions',
    images: 'Edit images',
};

const OPTIONAL_FIELDS = [
    { key: 'description', label: 'Description', hint: 'Bloom Assist can write this for you later' },
    { key: 'metal_type', label: 'Metal type (e.g. gold)' },
    { key: 'metal_purity', label: 'Metal purity (e.g. 18k)' },
    { key: 'metal_weight_g', label: 'Metal weight (g)', type: 'number' },
    { key: 'country_of_origin', label: 'Country of origin' },
    { key: 'care_info', label: 'Recommended care' },
];


// Minimal CSV line splitter that respects double-quoted fields (URLs are safe,
// but a quoted field may legitimately contain a comma).
const splitCsvLine = (line) => {
    const out = [];
    let cur = '', q = false;
    for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (q) {
            if (c === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else q = false; }
            else cur += c;
        } else if (c === '"') q = true;
        else if (c === ',') { out.push(cur); cur = ''; }
        else cur += c;
    }
    out.push(cur);
    return out;
};

const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (!lines.length) return [];
    const header = splitCsvLine(lines[0]).map(h => h.trim().toLowerCase());
    const iName = header.indexOf('name');
    const iImg = header.findIndex(h => h === 'image_url' || h === 'image');
    const iCert = header.findIndex(h => ['certificate_urls', 'certificates', 'certificate_url'].includes(h));
    const iPrice = header.indexOf('price');
    const iCur = header.findIndex(h => h === 'currency' || h === 'ccy');
    // Fall back to positional (name, image_url, certificate_urls) if no header match.
    const hasHeader = iName >= 0;
    const body = hasHeader ? lines.slice(1) : lines;
    return body.map(line => {
        const cols = splitCsvLine(line);
        const name = (hasHeader ? cols[iName] : cols[0]) || '';
        const img = (hasHeader ? (iImg >= 0 ? cols[iImg] : '') : cols[1]) || '';
        const certRaw = (hasHeader ? (iCert >= 0 ? cols[iCert] : '') : cols[2]) || '';
        return {
            name: name.trim(),
            image_url: img.trim(),
            certificate_urls: certRaw.split(';').map(s => s.trim()).filter(Boolean),
            // Price/currency only via a header column (not positional); backend
            // cleans price and normalizes currency (blank -> USD).
            price: (hasHeader && iPrice >= 0 ? (cols[iPrice] || '') : '').trim(),
            currency: (hasHeader && iCur >= 0 ? (cols[iCur] || '') : '').trim().toUpperCase(),
        };
    }).filter(r => r.name);
};

const ManageCatalogue = ({ token, onImported, defaultCurrency }) => {
    // The Editor opens on the 'enrich' category, with its first pill already
    // selected ("Fill in missing details"). An option is ALWAYS selected — there
    // is no placeholder state.
    const [section, setSection] = useState('enrich');   // 'create' | 'enrich' category
    const [tab, setTab] = useState(firstTabOf('enrich'));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [report, setReport] = useState(null);

    // CSV / JSON
    const [rows, setRows] = useState(null);   // parsed preview
    const [jsonText, setJsonText] = useState('');

    // Manual
    const [manual, setManual] = useState({ name: '', image: null, certs: [], showOptional: false, price: '', currency: 'USD' });

    // Pre-fill the manual price currency with the brand's default (once it loads),
    // so it matches by default and only differs if the merchant deliberately changes it.
    useEffect(() => {
        if (defaultCurrency) setManual(m => ({ ...m, currency: defaultCurrency }));
    }, [defaultCurrency]);

    // Currency options for the manual-add dropdown — served by the backend (Babel
    // names over our FX-covered currencies), so there's no client-side currency lib.
    const [currencyOptions, setCurrencyOptions] = useState([]);
    useEffect(() => { apiFetch('/api/currencies').then(setCurrencyOptions).catch(() => {}); }, []);

    // Enrich existing products (content-agent queue over products that have an
    // image + certificate but no description).
    const [needsList, setNeedsList] = useState(null);   // null = not loaded
    const [listLoading, setListLoading] = useState(false);
    const [idx, setIdx] = useState(0);
    const [draft, setDraft] = useState(null);           // null = (re)generate
    const [savedCount, setSavedCount] = useState(0);    // approvals only (NOT skips)

    // Which mode of each enrich flow is shown (toggle at the top of the panel).
    // Both default to the one-at-a-time review flow: a brand should see and approve
    // each draft before it lands, rather than opening on a bulk/blind action.
    const [fillView, setFillView] = useState('review');      // 'bulk' | 'review'
    const [enhanceView, setEnhanceView] = useState('queue'); // 'id' | 'queue'

    // Bulk fill (auto-fill all missing in parallel, then sample for review).
    const [bulk, setBulk] = useState(null);             // {total, done, running, samples, filled}
    // Enhance by passport ID.
    const [passportId, setPassportId] = useState('');
    const [idItem, setIdItem] = useState(null);         // product resolved from a passport id
    const [idDraft, setIdDraft] = useState(null);
    const [idBusy, setIdBusy] = useState(false);
    const [idError, setIdError] = useState(null);

    // Enhance product images by passport ID (its own state so it never bleeds
    // into the description-enhance flow above).
    const [imgId, setImgId] = useState('');
    const [imgItem, setImgItem] = useState(null);       // product resolved from a passport id
    const [imgBusy, setImgBusy] = useState(false);      // resolving the passport id
    const [imgError, setImgError] = useState(null);
    const [imgResult, setImgResult] = useState(null);   // {busy, error, mode, dataUrl, saved}

    const resetResult = () => { setError(null); setReport(null); };

    const loadNeeds = useCallback(async (scope = 'missing') => {
        setListLoading(true);
        try {
            const list = await apiFetch(`/api/products/needs-content?scope=${scope}`, { token });
            setNeedsList(list); setIdx(0); setDraft(null); setSavedCount(0);
        } catch {
            setNeedsList([]);
        } finally {
            setListLoading(false);
        }
    }, [token]);

    // The Editor opens straight onto "Fill in missing details", so load that queue
    // on mount. switchTab() only loads when the user *changes* option, which would
    // leave the default tab stuck on "Finding products…" forever.
    useEffect(() => { loadNeeds('missing'); }, [loadNeeds]);

    const current = needsList && idx < needsList.length ? needsList[idx] : null;

    const genDraft = useCallback(async (productId) => {
        setDraft({ busy: true, error: null, title: '', description: '' });
        try {
            const d = await apiFetch(`/api/products/${productId}/generate-content`, { method: 'POST', token });
            setDraft({ busy: false, error: null, title: d.title || '', description: d.description || '' });
        } catch (err) {
            setDraft({ busy: false, error: err.message, title: '', description: '' });
        }
    }, [token]);

    const advance = () => { setDraft(null); setIdx(i => i + 1); };

    const approveDraft = async () => {
        if (!current || !draft) return;
        setDraft(d => ({ ...d, busy: true, error: null }));
        try {
            // Write the English mirror too — the passport renders name_en/
            // description_en by default (see Passport.js).
            await apiFetch(`/api/products/${current.product_id}`, {
                method: 'PUT', token,
                body: {
                    name: draft.title, description: draft.description,
                    name_en: draft.title, description_en: draft.description, detected_lang: 'en',
                },
            });
            if (onImported) onImported();
            setSavedCount(c => c + 1);
            advance();
        } catch (err) {
            setDraft(d => ({ ...d, busy: false, error: err.message }));
        }
    };

    const putContent = (productId, title, description) => apiFetch(`/api/products/${productId}`, {
        method: 'PUT', token,
        body: { name: title, description, name_en: title, description_en: description, detected_lang: 'en' },
    });

    // Bulk fill: generate + save a description for EVERY missing product IN
    // PARALLEL (only fills blanks, so nothing is overwritten), then random-sample
    // up to 5 for review.
    const runBulk = async () => {
        const list = needsList || [];
        if (!list.length) return;
        setBulk({ total: list.length, done: 0, running: true, samples: [], filled: 0 });
        let done = 0;
        const results = await Promise.all(list.map(async (p) => {
            try {
                const d = await apiFetch(`/api/products/${p.product_id}/generate-content`, { method: 'POST', token });
                await putContent(p.product_id, d.title, d.description);
                return { ...p, title: d.title, description: d.description };
            } catch {
                return null;
            } finally {
                done += 1;
                setBulk(b => (b ? { ...b, done } : b));
            }
        }));
        const filled = results.filter(Boolean);
        if (onImported) onImported();
        const sample = [...filled].sort(() => Math.random() - 0.5).slice(0, 5);
        setBulk({ total: list.length, done: list.length, running: false, samples: sample, filled: filled.length });
    };

    const saveSample = async (i) => {
        const s = bulk.samples[i];
        try {
            await putContent(s.product_id, s.title, s.description);
            setBulk(b => ({ ...b, samples: b.samples.map((x, j) => (j === i ? { ...x, saved: true } : x)) }));
        } catch { /* leave unsaved */ }
    };

    // Enhance by passport ID.
    const genIdDraft = useCallback(async (productId) => {
        setIdDraft({ busy: true, error: null, title: '', description: '', saved: false });
        try {
            const d = await apiFetch(`/api/products/${productId}/generate-content`, { method: 'POST', token });
            setIdDraft({ busy: false, error: null, title: d.title || '', description: d.description || '', saved: false });
        } catch (err) {
            setIdDraft({ busy: false, error: err.message, title: '', description: '', saved: false });
        }
    }, [token]);

    const findByPassport = async () => {
        const id = passportId.trim();
        if (!id) return;
        setIdBusy(true); setIdError(null); setIdItem(null); setIdDraft(null);
        try {
            const p = await apiFetch(`/api/products/by-passport/${encodeURIComponent(id)}`, { token });
            if (!p.has_image) { setIdError('That product has no image to enhance from.'); }
            else { setIdItem(p); genIdDraft(p.product_id); }
        } catch (err) {
            setIdError(err.message);
        } finally {
            setIdBusy(false);
        }
    };

    const approveIdDraft = async () => {
        if (!idItem || !idDraft) return;
        setIdDraft(d => ({ ...d, busy: true, error: null }));
        try {
            await putContent(idItem.product_id, idDraft.title, idDraft.description);
            if (onImported) onImported();
            setIdDraft(d => ({ ...d, busy: false, saved: true }));
        } catch (err) {
            setIdDraft(d => ({ ...d, busy: false, error: err.message }));
        }
    };

    // ── Enhance product images ──
    const findImgByPassport = async () => {
        const id = imgId.trim();
        if (!id) return;
        setImgBusy(true); setImgError(null); setImgItem(null); setImgResult(null);
        try {
            const p = await apiFetch(`/api/products/by-passport/${encodeURIComponent(id)}`, { token });
            if (!p.has_image) { setImgError('That product has no image to enhance.'); }
            else { setImgItem(p); }
        } catch (err) {
            setImgError(err.message);
        } finally {
            setImgBusy(false);
        }
    };

    const runImgEnhance = async (mode) => {
        if (!imgItem) return;
        setImgResult({ busy: true, error: null, requested: mode, mode, dataUrl: null, saved: false });
        try {
            const res = await apiFetch(`/api/products/${imgItem.product_id}/enhance-image`, {
                method: 'POST', token, body: { mode },
            });
            // Backend echoes the mode it actually ran (match_catalogue falls back
            // to elevated_white with a message when there aren't 3 catalogue refs).
            setImgResult({ busy: false, error: null, requested: mode, mode: res.mode, message: res.message || null, dataUrl: `data:${res.mime};base64,${res.image_b64}`, saved: false });
        } catch (err) {
            setImgResult({ busy: false, error: err.message, requested: mode, mode, dataUrl: null, saved: false });
        }
    };

    const approveImg = async () => {
        if (!imgItem || !imgResult || !imgResult.dataUrl) return;
        setImgResult(r => ({ ...r, busy: true, error: null }));
        try {
            // Save by re-uploading the previewed image through the normal image
            // endpoint, so there's a single write path for a product's image.
            const blob = await (await fetch(imgResult.dataUrl)).blob();
            const ext = imgResult.mime === 'image/jpeg' ? 'jpg' : 'png';
            const fd = new FormData();
            fd.append('image', blob, `enhanced.${ext}`);
            await apiFetch(`/api/products/${imgItem.product_id}/image`, { method: 'POST', token, body: fd, isForm: true });
            setImgItem(it => ({ ...it, image_url: imgResult.dataUrl }));   // reflect new image at once
            setImgResult(r => ({ ...r, busy: false, saved: true }));
            if (onImported) onImported();
        } catch (err) {
            setImgResult(r => ({ ...r, busy: false, error: err.message }));
        }
    };

    const onCsvFile = (e) => {
        resetResult();
        const file = e.target.files[0];
        if (!file) { setRows(null); return; }
        const reader = new FileReader();
        reader.onload = () => {
            try { setRows(parseCsv(String(reader.result))); }
            catch { setError('Could not parse that CSV.'); setRows(null); }
        };
        reader.readAsText(file);
    };

    const parseJson = () => {
        resetResult();
        try {
            const data = JSON.parse(jsonText);
            const list = Array.isArray(data) ? data : data.products;
            if (!Array.isArray(list)) throw new Error('bad shape');
            setRows(list.map(r => ({
                name: (r.name || '').trim(),
                image_url: r.image_url || '',
                certificate_urls: Array.isArray(r.certificate_urls)
                    ? r.certificate_urls
                    : (typeof r.certificate_urls === 'string' ? r.certificate_urls.split(';').map(s => s.trim()).filter(Boolean) : []),
                // price/currency are optional; backend cleans price and normalizes
                // currency (blank -> USD).
                price: r.price ?? '',
                currency: (r.currency || '').toString().trim().toUpperCase(),
            })).filter(r => r.name));
        } catch {
            setError('That isn’t valid JSON. Expect an array of {name, image_url, certificate_urls, price, currency}.');
            setRows(null);
        }
    };

    const runImport = async () => {
        if (!rows || !rows.length) return;
        setBusy(true); resetResult();
        try {
            const res = await apiFetch('/api/products/import', { method: 'POST', token, body: { products: rows } });
            setReport(res);
            setRows(null);
            if (onImported) onImported();
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const submitManual = async () => {
        if (!manual.name.trim()) { setError('A product name is required.'); return; }
        // Confirm when the entered currency differs from the brand's default.
        if (defaultCurrency && manual.currency && manual.currency !== defaultCurrency &&
            !window.confirm(`This product's currency (${manual.currency}) is different from your brand's default currency (${defaultCurrency}). Add it anyway?`)) {
            return;
        }
        setBusy(true); resetResult();
        try {
            const body = { name: manual.name.trim(), currency: manual.currency };
            if (manual.price !== '' && manual.price != null) body.price = manual.price;
            for (const f of OPTIONAL_FIELDS) {
                const v = manual[f.key];
                if (v !== undefined && v !== '' && v !== null) body[f.key] = v;
            }
            const created = await apiFetch('/api/products', { method: 'POST', token, body });
            if (manual.image) {
                const fd = new FormData();
                fd.append('image', manual.image);
                await apiFetch(`/api/products/${created.product_id}/image`, { method: 'POST', token, body: fd, isForm: true });
            }
            for (const cert of manual.certs) {
                const fd = new FormData();
                fd.append('document', cert);
                fd.append('doc_type', 'certificate');
                await apiFetch(`/api/products/${created.product_id}/documents`, { method: 'POST', token, body: fd, isForm: true });
            }
            setReport({ created_count: 1, failed_count: 0, created: [{ name: created.name }], failed: [] });
            setManual({ name: '', image: null, certs: [], showOptional: false });
            if (onImported) onImported();
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const switchTab = (k) => {
        setTab(k); resetResult(); setRows(null);
        setBulk(null); setPassportId(''); setIdItem(null); setIdDraft(null); setIdError(null);
        setImgId(''); setImgItem(null); setImgError(null); setImgResult(null);
        setNeedsList(null); setDraft(null); setIdx(0); setSavedCount(0);
        if (k === 'enrich') loadNeeds('missing');
        if (k === 'enhance') loadNeeds('existing');
        if (k) track('brand', `manage_catalogue_tab:${k}`);
    };

    // Switching category immediately opens that category's FIRST option, rather
    // than clearing the panel and waiting for a pick.
    const selectSection = (s) => { setSection(s); switchTab(firstTabOf(s)); track('brand', `manage_catalogue_section:${s}`); };

    // The one-at-a-time queue, shared by "Review each one" (fill) and "Review the
    // queue" (enhance). Generates ON CLICK, not automatically.
    const queuePanel = (isEnhance) => {
        if (listLoading || needsList === null) return <p className="catalogue-hint">Finding products…</p>;
        if (needsList.length === 0) return <p className="catalogue-hint">{isEnhance ? 'No products with a description to enhance yet.' : 'No products are missing a description.'}</p>;
        if (!current) return (
            <div className="assistant-draft-done">
                <p className="catalogue-hint">{savedCount > 0 ? `Done — updated ${savedCount} product${savedCount === 1 ? '' : 's'}.` : 'Nothing updated.'}</p>
                <button className="catalogue-btn-secondary" onClick={() => loadNeeds(isEnhance ? 'existing' : 'missing')}>Check again</button>
            </div>
        );
        return (
            <>
                <p className="catalogue-hint">Product {idx + 1} of {needsList.length}</p>
                <div className="assistant-draft-head">
                    {current.image_url && <img className="assistant-draft-thumb" src={current.image_url} alt={current.name} />}
                    <div className="assistant-draft-name" title={current.name}>{current.name}</div>
                </div>
                {current.description && (
                    <div className="catalogue-current"><div className="catalogue-nice">Current description</div><p className="catalogue-current-text">{current.description}</p></div>
                )}
                {!draft ? (
                    <div className="assistant-draft-actions">
                        <button className="catalogue-btn-primary" onClick={() => genDraft(current.product_id)}>{isEnhance ? 'Suggest improved description' : 'Generate description'}</button>
                        <button className="catalogue-btn-secondary" onClick={advance}>Skip</button>
                    </div>
                ) : draft.busy && !draft.title && !draft.error ? (
                    <p className="catalogue-hint">{isEnhance ? 'Refining…' : 'Drafting…'}</p>
                ) : draft.error ? (
                    <>
                        <p className="dpp-error">{draft.error}</p>
                        <div className="assistant-draft-actions">
                            <button className="catalogue-btn-secondary" onClick={() => genDraft(current.product_id)}>Try again</button>
                            <button className="catalogue-btn-secondary" onClick={advance}>Skip</button>
                        </div>
                    </>
                ) : (
                    <>
                        <label className="dpp-transfer-label">Title</label>
                        <input className="dpp-input" value={draft.title} onChange={e => setDraft(d => ({ ...d, title: e.target.value }))} />
                        <label className="dpp-transfer-label">Description</label>
                        <textarea className="dpp-input" rows={5} value={draft.description} onChange={e => setDraft(d => ({ ...d, description: e.target.value }))} />
                        <div className="assistant-draft-actions">
                            <button className="catalogue-btn-primary" onClick={approveDraft} disabled={draft.busy || !draft.description.trim()}>{draft.busy ? 'Saving…' : 'Approve & save'}</button>
                            <button className="catalogue-btn-secondary" onClick={() => genDraft(current.product_id)} disabled={draft.busy}>Regenerate</button>
                            <button className="catalogue-btn-secondary" onClick={advance} disabled={draft.busy}>Skip</button>
                        </div>
                    </>
                )}
            </>
        );
    };

    const bulkPanel = (
        <>
            {!bulk ? (
                <>
                    <p className="catalogue-hint">
                        {listLoading || needsList === null ? 'Counting…' : `${(needsList || []).length} product${(needsList || []).length === 1 ? '' : 's'} missing a description.`}
                    </p>
                    <button className="catalogue-btn-primary" onClick={runBulk} disabled={listLoading || !(needsList && needsList.length)}>Fill all</button>
                </>
            ) : bulk.running ? (
                <p className="catalogue-hint">Filling… {bulk.done} of {bulk.total}.</p>
            ) : (
                <>
                    <p className="catalogue-hint">Filled {bulk.filled} product{bulk.filled === 1 ? '' : 's'}. A sample to review:</p>
                    {bulk.samples.map((s, i) => (
                        <div className="catalogue-sample" key={s.product_id}>
                            <div className="assistant-draft-head">
                                {s.image_url && <img className="assistant-draft-thumb" src={s.image_url} alt={s.name} />}
                                <div className="assistant-draft-name" title={s.name}>{s.name}</div>
                            </div>
                            <label className="dpp-transfer-label">Title</label>
                            <input className="dpp-input" value={s.title}
                                onChange={e => setBulk(b => ({ ...b, samples: b.samples.map((x, j) => (j === i ? { ...x, title: e.target.value, saved: false } : x)) }))} />
                            <label className="dpp-transfer-label">Description</label>
                            <textarea className="dpp-input" rows={3} value={s.description}
                                onChange={e => setBulk(b => ({ ...b, samples: b.samples.map((x, j) => (j === i ? { ...x, description: e.target.value, saved: false } : x)) }))} />
                            <button className="catalogue-btn-secondary" onClick={() => saveSample(i)}>{s.saved ? 'Saved' : 'Save'}</button>
                        </div>
                    ))}
                    <button className="catalogue-btn-secondary" onClick={() => { setBulk(null); loadNeeds('missing'); }}>Done</button>
                </>
            )}
        </>
    );

    const idPanel = (
        <>
            <label className="dpp-transfer-label">Passport ID</label>
            <div className="catalogue-id-row">
                <input className="dpp-input" placeholder="e.g. c273046d9666" value={passportId} onChange={e => setPassportId(e.target.value)} />
                <button className="catalogue-btn-primary" onClick={findByPassport} disabled={idBusy || !passportId.trim()}>{idBusy ? 'Finding…' : 'Find'}</button>
            </div>
            {idError && <p className="dpp-error">{idError}</p>}
            {idItem && (
                <>
                    <div className="assistant-draft-head">
                        {idItem.image_url && <img className="assistant-draft-thumb" src={idItem.image_url} alt={idItem.name} />}
                        <div className="assistant-draft-name">{idItem.name}</div>
                    </div>
                    {idItem.description && <div className="catalogue-current"><div className="catalogue-nice">Current description</div><p className="catalogue-current-text">{idItem.description}</p></div>}
                    {idDraft && idDraft.busy && !idDraft.title && !idDraft.error ? (
                        <p className="catalogue-hint">{idItem.description ? 'Refining…' : 'This product does not have a description yet — generating one…'}</p>
                    ) : idDraft && idDraft.error ? (
                        <><p className="dpp-error">{idDraft.error}</p><button className="catalogue-btn-secondary" onClick={() => genIdDraft(idItem.product_id)}>Try again</button></>
                    ) : idDraft ? (
                        <>
                            {!idItem.description && <p className="catalogue-nice">This product did not have a description, so we have generated one for you. Review and approve to save.</p>}
                            <label className="dpp-transfer-label">Title</label>
                            <input className="dpp-input" value={idDraft.title} onChange={e => setIdDraft(d => ({ ...d, title: e.target.value }))} />
                            <label className="dpp-transfer-label">Description</label>
                            <textarea className="dpp-input" rows={5} value={idDraft.description} onChange={e => setIdDraft(d => ({ ...d, description: e.target.value }))} />
                            {idDraft.saved && <p className="catalogue-nice">Saved.</p>}
                            <div className="assistant-draft-actions">
                                <button className="catalogue-btn-primary" onClick={approveIdDraft} disabled={idDraft.busy || !idDraft.description.trim()}>{idDraft.busy ? 'Saving…' : 'Approve & save'}</button>
                                <button className="catalogue-btn-secondary" onClick={() => genIdDraft(idItem.product_id)} disabled={idDraft.busy}>Regenerate</button>
                            </div>
                        </>
                    ) : null}
                </>
            )}
        </>
    );

    const imagesPanel = (
        <>
            <p className="catalogue-mode-desc">Edit one specific product.</p>
            <label className="dpp-transfer-label">Passport ID</label>
            <div className="catalogue-id-row">
                <input className="dpp-input" placeholder="e.g. c273046d9666" value={imgId} onChange={e => setImgId(e.target.value)} />
                <button className="catalogue-btn-primary" onClick={findImgByPassport} disabled={imgBusy || !imgId.trim()}>{imgBusy ? 'Finding…' : 'Find'}</button>
            </div>
            {imgError && <p className="dpp-error">{imgError}</p>}
            {imgItem && (
                <>
                    <p className="catalogue-mode-desc">Edit a product photo's background — match your catalogue or drop it on clean white. Preview before you save.</p>
                    <div className="assistant-draft-head">
                        {imgItem.image_url && <img className="assistant-draft-thumb" src={imgItem.image_url} alt={imgItem.name} />}
                        <div className="assistant-draft-name">{imgItem.name}</div>
                    </div>
                    <div className="assistant-draft-actions">
                        {IMAGE_MODES.map(m => (
                            <button
                                key={m.key}
                                className={imgResult && imgResult.requested === m.key ? 'catalogue-btn-primary' : 'catalogue-btn-secondary'}
                                onClick={() => runImgEnhance(m.key)}
                                disabled={imgResult && imgResult.busy}
                                title={m.hint}
                            >
                                {m.label}
                            </button>
                        ))}
                    </div>

                    {imgResult && imgResult.busy && !imgResult.dataUrl ? (
                        <p className="catalogue-hint">Enhancing…</p>
                    ) : imgResult && imgResult.error ? (
                        <p className="dpp-error">{imgResult.error}</p>
                    ) : imgResult && imgResult.dataUrl ? (
                        <>
                            {imgResult.message && (
                                <p className="catalogue-hint">{imgResult.message}</p>
                            )}
                            <div className="catalogue-image-compare">
                                <figure className="catalogue-compare-fig">
                                    <figcaption className="catalogue-nice">Before</figcaption>
                                    <img className="catalogue-compare-img" src={imgItem.image_url} alt="before" />
                                </figure>
                                <figure className="catalogue-compare-fig">
                                    <figcaption className="catalogue-nice">After</figcaption>
                                    <img className="catalogue-compare-img" src={imgResult.dataUrl} alt="after" />
                                </figure>
                            </div>
                            {imgResult.saved
                                ? <p className="catalogue-nice">Saved.</p>
                                : (
                                    <div className="assistant-draft-actions">
                                        <button className="catalogue-btn-primary" onClick={approveImg} disabled={imgResult.busy}>{imgResult.busy ? 'Saving…' : 'Approve & save'}</button>
                                    </div>
                                )}
                        </>
                    ) : null}
                </>
            )}
        </>
    );

    return (
        <div className="catalogue-editor">
            <div className="catalogue-toggle">
                <button className={`catalogue-tab${section === 'enrich' ? ' is-active' : ''}`} onClick={() => selectSection('enrich')}>Enrich your products</button>
                <button className={`catalogue-tab${section === 'create' ? ' is-active' : ''}`} onClick={() => selectSection('create')}>Create new products</button>
            </div>

            <div className="catalogue-suboptions">
                {SECTION_TABS[section].map(t => (
                    <button key={t.key} className={`catalogue-hub-action${tab === t.key ? ' is-active' : ''}`} onClick={() => switchTab(t.key)}>{t.label}</button>
                ))}
            </div>

            <div className="catalogue-detail">
                <div className="dpp-card insights-card assistant-card catalogue-panel">
                    <div className="insights-card-title">{DETAIL_TITLES[tab]}</div>
                    <div className="catalogue-content">
                    {/* ── CSV ── */}
                    {tab === 'csv' && (
                        <div className="catalogue-body">
                            <p className="catalogue-hint">
                                Columns: <code>name, image_url, certificate_urls, price, currency</code>. Separate multiple certificate links with a semicolon; <code>currency</code> is a 3-letter code (e.g. USD) — blank uses your brand's default currency.
                            </p>
                            <input type="file" accept=".csv,text/csv" onChange={onCsvFile} />
                        </div>
                    )}

                    {/* ── JSON ── */}
                    {tab === 'json' && (
                        <div className="catalogue-body">
                            <p className="catalogue-hint">
                                An array of <code>{'{ name, image_url, certificate_urls: [], price, currency }'}</code> — the same shape the API accepts. <code>price</code>/<code>currency</code> are optional (blank currency uses your brand's default).
                            </p>
                            <textarea
                                className="dpp-input catalogue-json"
                                rows={6}
                                placeholder='[{"name": "Emerald Ring", "image_url": "https://…", "certificate_urls": ["https://…"], "price": 4500, "currency": "USD"}]'
                                value={jsonText}
                                onChange={e => setJsonText(e.target.value)}
                            />
                            <button className="catalogue-btn-primary" onClick={parseJson} disabled={!jsonText.trim()}>Preview</button>
                        </div>
                    )}

                    {/* ── Manual ── */}
                    {tab === 'manual' && (
                        <div className="catalogue-body">
                            <label className="dpp-transfer-label">Product image <span className="catalogue-nice">— recommended, lets Bloom enhance product information</span></label>
                            <input type="file" accept="image/*" onChange={e => { resetResult(); setManual(m => ({ ...m, image: e.target.files[0] || null })); }} />

                            <label className="dpp-transfer-label">Certificates <span className="catalogue-nice">— recommended</span></label>
                            <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png" onChange={e => { resetResult(); setManual(m => ({ ...m, certs: Array.from(e.target.files) })); }} />

                            <label className="dpp-transfer-label">Name *</label>
                            <input
                                className="dpp-input"
                                placeholder="Product name"
                                value={manual.name}
                                onChange={e => setManual(m => ({ ...m, name: e.target.value }))}
                            />

                            <label className="dpp-transfer-label">Price <span className="catalogue-nice">— at purchase, for portfolio valuation</span></label>
                            <div className="catalogue-price-row">
                                <input
                                    className="dpp-input catalogue-price-amount"
                                    type="number" step="any" min="0" placeholder="0.00"
                                    value={manual.price}
                                    onChange={e => setManual(m => ({ ...m, price: e.target.value }))}
                                />
                                <input
                                    className="dpp-input catalogue-price-cur"
                                    list="catalogue-currencies"
                                    placeholder={defaultCurrency || 'USD'}
                                    value={manual.currency}
                                    onChange={e => setManual(m => ({ ...m, currency: e.target.value.toUpperCase() }))}
                                />
                                <datalist id="catalogue-currencies">
                                    {currencyOptions.map(o => (
                                        <option key={o.code} value={o.code}>{o.name}</option>
                                    ))}
                                </datalist>
                            </div>

                            <button className="catalogue-optional-toggle" onClick={() => setManual(m => ({ ...m, showOptional: !m.showOptional }))}>
                                {manual.showOptional ? '– Hide optional details' : '+ Optional details'}
                            </button>
                            {manual.showOptional && OPTIONAL_FIELDS.map(f => (
                                <div key={f.key}>
                                    <label className="dpp-transfer-label">{f.label} <span className="catalogue-nice">optional</span></label>
                                    {f.key === 'description'
                                        ? <textarea className="dpp-input" rows={3} value={manual[f.key] || ''} onChange={e => setManual(m => ({ ...m, [f.key]: e.target.value }))} />
                                        : <input className="dpp-input" type={f.type || 'text'} step={f.type === 'number' ? 'any' : undefined} value={manual[f.key] || ''} onChange={e => setManual(m => ({ ...m, [f.key]: e.target.value }))} />}
                                </div>
                            ))}

                            <button className="catalogue-btn-primary" onClick={submitManual} disabled={busy || !manual.name.trim()}>
                                {busy ? 'Adding…' : 'Add to catalogue'}
                            </button>
                        </div>
                    )}

                    {/* ── Fill in missing details: pick a mode at the top ── */}
                    {tab === 'enrich' && (
                        <div className="catalogue-mode-wrap">
                            <div className="catalogue-toggle">
                                <button className={`catalogue-tab${fillView === 'review' ? ' is-active' : ''}`} onClick={() => setFillView('review')}>Review each one</button>
                                <button className={`catalogue-tab${fillView === 'bulk' ? ' is-active' : ''}`} onClick={() => setFillView('bulk')}>Bulk fill</button>
                            </div>
                            {fillView === 'bulk' ? (
                                <>
                                    <p className="catalogue-mode-desc">Fill every missing description automatically, then review a sample.</p>
                                    {bulkPanel}
                                </>
                            ) : (
                                <>
                                    <p className="catalogue-mode-desc">Step through products and approve each.</p>
                                    {queuePanel(false)}
                                </>
                            )}
                        </div>
                    )}

                    {/* ── Enhance existing descriptions: pick a mode at the top ── */}
                    {tab === 'enhance' && (
                        <div className="catalogue-mode-wrap">
                            <div className="catalogue-toggle">
                                <button className={`catalogue-tab${enhanceView === 'queue' ? ' is-active' : ''}`} onClick={() => setEnhanceView('queue')}>Review the queue</button>
                                <button className={`catalogue-tab${enhanceView === 'id' ? ' is-active' : ''}`} onClick={() => setEnhanceView('id')}>Passport ID</button>
                            </div>
                            {enhanceView === 'id' ? (
                                <>
                                    <p className="catalogue-mode-desc">Enhance one specific product.</p>
                                    {idPanel}
                                </>
                            ) : (
                                <>
                                    <p className="catalogue-mode-desc">Step through products that have a description.</p>
                                    {queuePanel(true)}
                                </>
                            )}
                        </div>
                    )}

                    {/* ── Enhance product images: enter a passport ID, pick a preset ── */}
                    {tab === 'images' && (
                        <div className="catalogue-body">
                            {imagesPanel}
                        </div>
                    )}

                    {/* Preview (CSV/JSON) */}
                    {(tab === 'csv' || tab === 'json') && rows && (
                        <div className="catalogue-preview">
                            <p className="catalogue-hint">{rows.length} product{rows.length === 1 ? '' : 's'} ready to import.</p>
                            <div className="catalogue-preview-list">
                                {rows.slice(0, 8).map((r, i) => (
                                    <div className="catalogue-preview-row" key={i}>
                                        <span className="catalogue-preview-name">{r.name}</span>
                                        <span className="catalogue-preview-meta">
                                            {r.image_url ? 'image' : 'no image'} · {r.certificate_urls.length} cert{r.certificate_urls.length === 1 ? '' : 's'}
                                        </span>
                                    </div>
                                ))}
                                {rows.length > 8 && <div className="catalogue-preview-more">+ {rows.length - 8} more…</div>}
                            </div>
                            <button className="catalogue-btn-primary" onClick={runImport} disabled={busy}>
                                {busy ? 'Importing…' : `Import ${rows.length} product${rows.length === 1 ? '' : 's'}`}
                            </button>
                        </div>
                    )}

                    {error && <p className="dpp-error">{error}</p>}

                    {report && (
                        <div className="catalogue-report">
                            <p className="catalogue-hint">
                                Added {report.created_count} product{report.created_count === 1 ? '' : 's'}
                                {report.failed_count ? `, ${report.failed_count} failed` : ''}.
                            </p>
                            {report.failed_count > 0 && (
                                <ul className="catalogue-failures">
                                    {report.failed.map((f, i) => (
                                        <li key={i}>Row {f.row}{f.name ? ` (${f.name})` : ''}: {f.error}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                        </div>
                    </div>
            </div>
        </div>
    );
};

export default ManageCatalogue;
